/**
 * useAuth Hook
 *
 * Custom hook for accessing authentication state and actions.
 * Wraps the Zustand authStore for easier consumption in components.
 *
 * @example
 * ```tsx
 * function LoginButton() {
 *   const { login, isLoading, isAuthenticated, user } = useAuth();
 *
 *   const handleLogin = async () => {
 *     try {
 *       await login('user@example.com', 'password');
 *     } catch (error) {
 *       console.error('Login failed:', error);
 *     }
 *   };
 *
 *   if (isAuthenticated) {
 *     return <div>Welcome, {user?.firstName}!</div>;
 *   }
 *
 *   return <Button onClick={handleLogin} disabled={isLoading}>Login</Button>;
 * }
 * ```
 */

import { useAuthStore } from '@/stores/authStore';
import { User } from '@/services/auth.service';

export interface UseAuthReturn {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
}

/**
 * Hook to access authentication state and actions
 */
export const useAuth = (): UseAuthReturn => {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const error = useAuthStore((state) => state.error);
  const login = useAuthStore((state) => state.login);
  const logout = useAuthStore((state) => state.logout);
  const updateUser = useAuthStore((state) => state.updateUser);

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    updateUser,
  };
};
